/* Zengin metnin temizleyicisi — sayfaya yazılan her metin buradan geçiyor.

   Editör metni tarayıcının `contenteditable` alanından alıyor; oradan gelen
   HTML ne olursa olabilir: yapıştırılmış bir Word belgesi, `<font>` etiketli
   eski komut çıktısı, `onerror`'lı bir resim. Belgeye yalnızca bu işlevin
   çıktısı yazılıyor, ve aynı işlev üç yerde çalışıyor: panelde (yazarken),
   API'de (kaydederken), üreticide (basarken). Üçünden biri atlansa diğer
   ikisi yakalar.

   **DOM'suz.** Worker'da `DOMParser` yok; Node'da da yok. Dize tabanlı bir
   belirteçleyici, izin listesi ve bir etiket yığını yetiyor, çünkü çıktının
   dili çok küçük:

     strong em u s sup sub br         biçim
     a[href, target]                  güvenli bağlantı
     span[style, class, lang]         yalnızca renk, vurgu rengi, boyut sınıfı, dil

   Geri kalan her etiket **açılır** (içindeki metin kalır, etiket gider);
   `script`, `style`, `iframe` gibi içeriği tehlikeli olanlar içerikleriyle
   birlikte **silinir**. Öznitelikler tek tek izin listesinden geçiyor —
   `on*`, `style="expression(...)"`, `href="javascript:"` hiçbir yoldan
   giremez.

   Çıktı **kanonik**: aynı görünümün tek bir yazılışı var (`<b>` → `<strong>`,
   `rgb()` → `#rrggbb`, kapanmamış etiket kapatılmış). Böylece taslağın
   canlıdan farklı olup olmadığını dize karşılaştırması doğru söylüyor. */

/** Metin boyutu sınıfları — site CSS'inde tanımlı (style.css, `.t-*`). */
export const TEXT_SIZES = ['t-xs', 't-sm', 't-lg', 't-xl', 't-2xl'] as const;
export type TextSize = (typeof TEXT_SIZES)[number];

const FORMAT: Record<string, string> = {
  strong: 'strong',
  b: 'strong',
  em: 'em',
  i: 'em',
  u: 'u',
  ins: 'u',
  s: 's',
  strike: 's',
  del: 's',
  sup: 'sup',
  sub: 'sub'
};

/** İçeriğiyle birlikte silinenler. */
/* `xml`: Word panoya belge özelliklerini `<xml>…</xml>` içinde koyuyor. */
const DROP = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'math', 'head', 'title', 'textarea', 'select', 'button', 'xml']);

/** Boşluk sayılan, satır kıran bloklar — açılırken yerine `<br>` konur. */
const BREAKS = new Set(['p', 'div', 'li', 'tr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote']);

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" };

function decode(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+\d*);/gi, (whole, name: string) => {
    const lower = name.toLowerCase();
    if (lower.startsWith('#x')) return safeChar(Number.parseInt(lower.slice(2), 16), whole);
    if (lower.startsWith('#')) return safeChar(Number.parseInt(lower.slice(1), 10), whole);
    return ENTITIES[lower] ?? whole;
  });
}

function safeChar(code: number, fallback: string): string {
  if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return fallback;
  return String.fromCodePoint(code);
}

export function escapeText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/ /g, '&nbsp;');
}

function escapeAttr(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Bağlantı: sayfa içi, site içi, https, e-posta, telefon. Başka hiçbir şema. */
export function safeUrl(raw: string): string | null {
  const v = decode(raw).trim();
  /* Telefon boşluk taşıyabilir; kalıbı rakam, boşluk ve +()- dışında hiçbir
     şeye izin vermediği için genel boşluk yasağından önce bakılıyor. */
  if (/^tel:\+?[0-9 ()-]+$/i.test(v)) return v;
  /* Kontrol karakterleri ve boşluk `java\tscript:` gibi kaçamakları kapatıyor. */
  if (/[\u0000-\u001f\u007f\s]/.test(v)) return null;
  if (/^#[\w-]*$/.test(v)) return v;
  if (/^\/(?!\/)[^\s]*$/.test(v)) return v;
  if (/^https:\/\/[^\s/]+[^\s]*$/i.test(v)) return v;
  if (/^mailto:[^\s]+$/i.test(v)) return v;
  /* Göreli sayfa: `blog.html`, `works.html#x`, `blog/yazi.html`. İki nokta
     üst üste yok, yani bir şema taşıyamaz; `..` de yok. */
  if (/^[a-z0-9][a-z0-9_-]*(\/[a-z0-9_-]+)*(\.html)?(#[\w-]*)?$/i.test(v)) return v;
  return null;
}

/** `#abc`, `#aabbcc`, `rgb(1, 2, 3)` → `#aabbcc`; başka her şey `null`. */
export function normalizeColor(raw: string): string | null {
  const v = raw.trim().toLowerCase();
  let m = /^#([0-9a-f]{3})$/.exec(v);
  if (m) return `#${[...m[1]!].map((c) => c + c).join('')}`;
  m = /^#([0-9a-f]{6})$/.exec(v);
  if (m) return v;
  m = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(?:1|1\.0+|0?\.\d+|0))?\s*\)$/.exec(v);
  if (m) {
    const parts = [m[1], m[2], m[3]].map((n) => Math.min(255, Number(n)));
    return `#${parts.map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  }
  return null;
}

function attrs(source: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)\s*(?:=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+)))?/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(source))) {
    out[m[1]!.toLowerCase()] = m[2] ?? m[3] ?? m[4] ?? '';
  }
  return out;
}

/** `span`/`font` özniteliklerinden izin verilen biçim: renk, vurgu, boyut. */
function spanFormat(tag: string, a: Record<string, string>): string | null {
  const style: string[] = [];
  let color: string | null = null;
  let background: string | null = null;
  for (const decl of decode(a['style'] ?? '').split(';')) {
    const [prop, ...rest] = decl.split(':');
    const value = rest.join(':');
    const name = prop?.trim().toLowerCase();
    if (name === 'color') color = normalizeColor(value) ?? color;
    if (name === 'background-color' || name === 'background') background = normalizeColor(value) ?? background;
  }
  if (tag === 'font' && a['color']) color = normalizeColor(a['color']) ?? color;
  if (color) style.push(`color:${color}`);
  if (background) style.push(`background-color:${background}`);
  const size = (a['class'] ?? '').split(/\s+/).find((c) => (TEXT_SIZES as readonly string[]).includes(c));
  /* Dil işareti: Türkçe metindeki İngilizce sözcük ("Studio") ekran
     okuyucuda doğru okunsun. Yalnızca iki değer. */
  const lang = tag === 'span' && (a['lang'] === 'en' || a['lang'] === 'tr') ? a['lang'] : null;
  if (style.length === 0 && !size && !lang) return null;
  return `<span${size ? ` class="${size}"` : ''}${lang ? ` lang="${lang}"` : ''}${style.length ? ` style="${style.join(';')}"` : ''}>`;
}

/**
 * Zengin metni izin listesine indirger. Girdi ne olursa olsun çıktı
 * güvenli, dengeli ve kanonik. Blok etiketleri (`p`, `div`, `li`…) satır
 * sonu olarak korunuyor: `contenteditable` Enter'ı tarayıcıya göre `<div>`
 * ya da `<p>` olarak yazıyor ve ikisi de aynı görünmeli. Tek satırlık
 * alanlar (düğme yazısı, alt metin) `sanitizePlain` kullanır.
 */
export function sanitizeRich(input: string): string {
  const out: string[] = [];
  /* Açık etiketlerin yığını: [kaynak ad, kanonik açılış, kanonik ad | null]. */
  const stack: { source: string; close: string | null }[] = [];
  let dropping: string | null = null;
  let dropDepth = 0;
  /* Etiket adında `:` olabilir: Word `<o:p>`, `<w:sdt>` yazıyor. Tanınmazsa
     sıradan bilinmeyen etiket gibi açılıyor — kaçışlı metin olarak sızmıyor. */
  const re = /<!--[\s\S]*?(?:-->|$)|<\/?([a-zA-Z][a-zA-Z0-9:-]*)((?:[^>"']|"[^"]*"|'[^']*')*)>|([^<]+)|(<)/g;
  let m: RegExpExecArray | null;

  while ((m = re.exec(input))) {
    const [whole, rawName, rawAttrs, text, stray] = m;
    if (whole.startsWith('<!--')) continue;
    if (text !== undefined || stray !== undefined) {
      if (!dropping) out.push(escapeText(decode(text ?? '<')));
      continue;
    }
    const name = rawName!.toLowerCase();
    const closing = whole.startsWith('</');
    const selfClosing = /\/\s*>$/.test(whole);

    if (dropping) {
      if (name === dropping) dropDepth += closing ? -1 : selfClosing ? 0 : 1;
      if (dropDepth === 0) dropping = null;
      continue;
    }
    if (DROP.has(name)) {
      if (!closing && !selfClosing) {
        dropping = name;
        dropDepth = 1;
      }
      continue;
    }

    if (name === 'br') {
      if (!closing) out.push('<br>');
      continue;
    }

    if (closing) {
      /* Yığında bu kaynak adı varsa ona kadar kapat; yoksa başıboş kapanış. */
      const at = stack.map((s) => s.source).lastIndexOf(name);
      if (at < 0) {
        if (BREAKS.has(name)) out.push('<br>');
        continue;
      }
      while (stack.length > at) {
        const top = stack.pop()!;
        if (top.close) out.push(`</${top.close}>`);
      }
      if (BREAKS.has(name)) out.push('<br>');
      continue;
    }

    if (selfClosing) continue;
    const a = attrs(rawAttrs ?? '');
    let open: string | null = null;
    let close: string | null = null;

    if (FORMAT[name]) {
      close = FORMAT[name]!;
      open = `<${close}>`;
    } else if (name === 'a') {
      const href = safeUrl(a['href'] ?? '');
      if (href) {
        const blank = (a['target'] ?? '').toLowerCase() === '_blank';
        open = `<a href="${escapeAttr(href)}"${blank ? ' target="_blank" rel="noopener noreferrer"' : ''}>`;
        close = 'a';
      }
    } else if (name === 'span' || name === 'font' || name === 'mark') {
      open = name === 'mark' ? '<span style="background-color:#fff1a8">' : spanFormat(name, a);
      if (open) close = 'span';
    }
    if (open) out.push(open);
    stack.push({ source: name, close });
  }
  while (stack.length) {
    const top = stack.pop()!;
    if (top.close) out.push(`</${top.close}>`);
  }
  return tidy(out.join(''));
}

/**
 * Boş biçim etiketlerini (`<strong></strong>`) ve baştaki/sondaki boş
 * satırları atar; art arda `<br>`'leri ikiye indirir.
 */
function tidy(html: string): string {
  let prev = '';
  let out = html;
  while (prev !== out) {
    prev = out;
    out = out.replace(/<(strong|em|u|s|sup|sub|span|a)\b[^>]*><\/\1>/g, '');
  }
  return out
    .replace(/(?:<br>){3,}/g, '<br><br>')
    .replace(/^(?:<br>|\s)+/, '')
    .replace(/(?:<br>|\s)+$/, '');
}

/** Hiçbir etiket: başlık kimliği, alt metin, düğme yazısı gibi alanlar için. */
export function sanitizePlain(input: string): string {
  return escapeText(decode(sanitizeRich(input).replace(/<br>/g, ' ').replace(/<[^>]+>/g, '')))
    .replace(/\s+/g, ' ')
    .trim();
}

/** Zengin metnin görünen metni — boş mu, kelime sayısı gibi sorular için. */
export function richToText(html: string): string {
  return decode(html.replace(/<br>/g, ' ').replace(/<[^>]+>/g, '')).replace(/\s+/g, ' ').trim();
}
